import React, { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { EventOption } from "../../types/public-types";
import { BarTask } from "../../types/bar-task";
import { Task } from "../../types/public-types";
import { getParentWbs } from "../gantt/gantt";
import { buildDefaultColumnWidths, MIN_COLUMN_WIDTH } from "./task-list-columns";
import styles from "./task-list.module.css";

export type TaskListProps = {
  headerHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  rowHeight: number;
  ganttHeight: number;
  scrollY: number;
  locale: string;
  tasks: Task[];
  scheduleType: string;
  leafTasks: Task[];
  taskListRef: React.RefObject<HTMLDivElement>;
  horizontalContainerClass?: string;
  selectedTask: BarTask | undefined;
  setSelectedTask: (task: string) => void;
  onExpanderClick: (task: Task) => void;
  taskLabelRenderer?: (Task: Task) => React.ReactNode;
  TaskListHeader: React.FC<{
    headerHeight: number;
    rowWidth: string;
    fontFamily: string;
    fontSize: string;
    scheduleType: string;
    allSelected?: boolean;
    onSelectAll?: (selected: boolean) => void;
    columnWidths?: Record<string, number>;
    onColumnResize?: (colId: string, width: number) => void;
  }>;
  TaskListTable: React.FC<{
    rowHeight: number;
    rowWidth: string;
    fontFamily: string;
    fontSize: string;
    locale: string;
    tasks: Task[];
    scheduleType: string;
    leafTasks: Task[];
    selectedTaskId: string;
    setSelectedTask: (taskId: string) => void;
    onExpanderClick: (task: Task) => void;
    selectedTasks?: string[];
    onTaskSelect?: (taskId: string, selected: boolean) => void;
    taskLabelRenderer?: (Task: Task) => React.ReactNode;
    virtualItems?: VirtualItem[];
    columnWidths?: Record<string, number>;
  }>;
  containerWidth?: number;
  containerMaxWidth?: number;
  onContentWidthChange?: (width: number) => void;
  innerScrollRef?: React.RefObject<HTMLDivElement>;
  horizontalContainerRef?: React.RefObject<HTMLDivElement>;
} & EventOption;

export const TaskList: React.FC<TaskListProps> = ({
  headerHeight,
  fontFamily,
  fontSize,
  rowWidth,
  rowHeight,
  scrollY,
  tasks,
  scheduleType,
  leafTasks,
  selectedTask,
  setSelectedTask,
  onExpanderClick,
  locale,
  ganttHeight,
  taskListRef,
  horizontalContainerClass,
  TaskListHeader,
  TaskListTable,
  taskLabelRenderer,
  onMultiSelect,
  containerWidth,
  containerMaxWidth,
  onContentWidthChange,
  innerScrollRef,
  horizontalContainerRef: externalHorizontalContainerRef,
}) => {
  const internalHorizontalContainerRef = useRef<HTMLDivElement>(null);
  const horizontalContainerRef = externalHorizontalContainerRef ?? internalHorizontalContainerRef;
  const headerScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rowsEl = horizontalContainerRef.current;
    if (!rowsEl) return;
    const onScroll = () => {
      if (headerScrollRef.current) {
        headerScrollRef.current.scrollLeft = rowsEl.scrollLeft;
      }
    };
    rowsEl.addEventListener("scroll", onScroll);
    return () => rowsEl.removeEventListener("scroll", onScroll);
  }, [horizontalContainerRef]);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);

  // Per-column widths (px), shared by the header and table so resizing a column
  // keeps both in sync. Seeded from the column factors × rowWidth.
  const hasCheckbox = !!onMultiSelect;
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    buildDefaultColumnWidths(scheduleType, hasCheckbox, rowWidth)
  );

  // Re-seed only when the column SET changes (scheduleType / multiselect adds or
  // removes a column), preserving widths the user already set for columns that
  // persist. Returning `prev` when the set is unchanged avoids needless renders.
  useEffect(() => {
    const defaults = buildDefaultColumnWidths(scheduleType, hasCheckbox, rowWidth);
    setColumnWidths(prev => {
      const ids = Object.keys(defaults);
      const sameSet =
        ids.length === Object.keys(prev).length && ids.every(id => id in prev);
      if (sameSet) return prev;
      const next: Record<string, number> = {};
      for (const id of ids) next[id] = prev[id] ?? defaults[id];
      return next;
    });
  }, [scheduleType, hasCheckbox, rowWidth]);

  const handleColumnResize = (colId: string, width: number) => {
    setColumnWidths(prev => ({
      ...prev,
      [colId]: Math.max(MIN_COLUMN_WIDTH, width),
    }));
  };

  // True total width of all columns. Reported up so the parent can cap the table
  // at its content (no white space past the last column) and size the horizontal
  // scrollbar exactly — both without relying on a laggy scrollWidth measurement.
  const contentWidth = useMemo(
    () => Object.values(columnWidths).reduce((sum, w) => sum + w, 0),
    [columnWidths]
  );
  useEffect(() => {
    onContentWidthChange?.(contentWidth);
  }, [contentWidth, onContentWidthChange]);

  // Identity of the rendered rows, not just how many. Deleting a whole page swaps
  // in a different page of the SAME length, which a `tasks.length` dependency
  // cannot see. Recomputing on every `tasks` reference is cheap, and the equal
  // string keeps effects from re-running when the parent just rebuilt the array.
  const taskIdsKey = useMemo(() => tasks.map(t => t.id).join("|"), [tasks]);

  const selectedTaskIds = useMemo(() => new Set(selectedTasks), [selectedTasks]);

  const [pendingTaskSelect, setPendingTaskSelect] = useState<{ taskId: string; selected: boolean }[]>([]);
  const prevSelectedTasksRef = useRef<string[]>([]);
  const expandedTasks = useRef<string[]>([]);

  const virtualizer = useVirtualizer({
    count: tasks.length,
    getScrollElement: () => horizontalContainerRef.current,
    estimateSize: () => rowHeight,
    overscan: 10
  });

  useEffect(() => {
    if (horizontalContainerRef.current) {
      horizontalContainerRef.current.scrollTop = scrollY;
    }
  }, [scrollY]);

  useEffect(() => {
    if (
      onMultiSelect &&
      JSON.stringify(prevSelectedTasksRef.current) !==
        JSON.stringify(selectedTasks)
    ) {
      const selectedTaskObjects = tasks.filter(task =>
        selectedTasks.includes(task.id)
      );
      prevSelectedTasksRef.current = [...selectedTasks];
      onMultiSelect(selectedTaskObjects);
    }
  }, [selectedTasks, tasks, onMultiSelect]);



  // process pending task selections
  useEffect(() => {
    if (pendingTaskSelect.length === 0) return;

    const newSelected = new Set(selectedTasks);
    pendingTaskSelect.forEach(({ taskId, selected }) => {
      if (selected) {
        // Expand the task and its children first
        recursiveOpen(taskId, tasks);
        // Collect descendants after expansion
        const descendants = getDescendants(taskId, tasks);
        newSelected.add(taskId);
        descendants.forEach(d => newSelected.add(d));
      } else {
        const descendants = getDescendants(taskId, tasks);
        newSelected.delete(taskId);
        descendants.forEach(d => newSelected.delete(d));
      }
    });

    setSelectedTasks(Array.from(newSelected));
    setPendingTaskSelect([]);
  }, [pendingTaskSelect]);

  // Handle tasks prop changes to update descendants of selected tasks
  useEffect(() => {
    expandedTasks.current = expandedTasks.current.filter(id => {
    const t = tasks.find(task => task.id === id);
    return t && !t.hideChildren;
    });
    if (selectedTasks.length === 0) return;

    const newSelected = new Set<string>();
    selectedTasks.forEach(taskId => {
      // Only include tasks that still exist
      if (tasks.find(t => t.id === taskId)) {
        recursiveOpen(taskId, tasks); // Ensure task is expanded
        newSelected.add(taskId);
        const descendants = getDescendants(taskId, tasks);
        descendants.forEach(d => newSelected.add(d));
      }
    });

    const newSelectedArray = Array.from(newSelected);
    if (JSON.stringify(newSelectedArray) !== JSON.stringify(selectedTasks)) {
      setSelectedTasks(newSelectedArray);
    }
  }, [taskIdsKey]);

  // Finds all descendant task IDs recursively using WBS hierarchy
  const getDescendants = (taskId: string, allTasks: Task[]): string[] => {
    const task = allTasks.find(t => t.id === taskId);
    if (!task) return [];
    const children = allTasks.filter(t => getParentWbs(t.id) === taskId);
    return children.flatMap(child => [child.id, ...getDescendants(child.id, allTasks)]);
  };

  const recursiveOpen = (taskId: string, allTasks: Task[]) => {
    const task = allTasks.find(t => t.id === taskId);
    if (!task) return;
    if (!expandedTasks.current.includes(taskId) && task.hideChildren) {
      onExpanderClick(task);
      expandedTasks.current = [...expandedTasks.current, taskId];
    }
    const children = allTasks.filter(t => getParentWbs(t.id) === taskId);
    children.forEach(c => recursiveOpen(c.id, allTasks));
  };

  const handleTaskSelect = (taskId: string, selected: boolean) => {
    setPendingTaskSelect(prev => [...prev, { taskId, selected }]);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedTasks(tasks.map(task => task.id));
      setPendingTaskSelect([]);
    } else {
      setSelectedTasks([]);
      setPendingTaskSelect([]);
    }
  };

  const headerProps = {
    headerHeight,
    fontFamily,
    fontSize,
    rowWidth,
    scheduleType,
    // membership, not count — a stale selection of the right size (every row on the
    // page deleted, an equally sized page loaded in) would otherwise read as "all"
    allSelected: tasks.length > 0 && tasks.every(t => selectedTaskIds.has(t.id)),
    onSelectAll: onMultiSelect ? handleSelectAll : undefined,
    columnWidths,
    onColumnResize: handleColumnResize,
  };
  const selectedTaskId = selectedTask ? selectedTask.id : "";

  const tableProps = {
    rowHeight,
    rowWidth,
    fontFamily,
    fontSize,
    tasks,
    leafTasks,
    scheduleType,
    locale,
    selectedTaskId: selectedTaskId,
    setSelectedTask,
    onExpanderClick,
    selectedTasks: onMultiSelect ? selectedTasks : undefined,
    onTaskSelect: onMultiSelect ? handleTaskSelect : undefined,
    taskLabelRenderer,
    virtualItems: virtualizer.getVirtualItems(),
    columnWidths,
  };

  return (
    <div
      ref={taskListRef}
      style={
        containerWidth != null || containerMaxWidth != null
          ? {
              ...(containerWidth != null ? { width: containerWidth } : {}),
              ...(containerMaxWidth != null ? { maxWidth: containerMaxWidth } : {}),
              overflow: "hidden",
              flexShrink: 0,
            }
          : {}
      }
    >
      <div
        ref={innerScrollRef}
        className={styles.hideScrollbar}
        style={{ overflowX: "hidden" }}
      >
        <div ref={headerScrollRef} style={{ overflow: "hidden" }}>
          <TaskListHeader {...headerProps} />
        </div>
        <div
          ref={horizontalContainerRef}
          className={horizontalContainerClass}
          style={ganttHeight ? { height: ganttHeight } : {}}
        >
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            <TaskListTable {...tableProps} />
          </div>
        </div>
      </div>
    </div>
  );
};
